import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import {
  getPolicyViewHeaderPartyResponse,
  getPolicyViewHeaderResponse,
} from "../../../../redux/features/policyDetailsSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const CyberSecurityCancellationReviewPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { handleNextSubstep } = usePolicyCancellationFlow();

  const { t } = useTranslation();

  // Get policy detail from Redux
  const policyDetailsInfo = useSelector(getPolicyViewHeaderResponse);
  const policyDetailsPartyInfo = useSelector(getPolicyViewHeaderPartyResponse);

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== CYBER SECURITY POLICY CANCEL DATA DEBUG ===");
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2)
  );
  console.log(
    "Full policyDetailsPartyInfo:",
    JSON.stringify(policyDetailsPartyInfo, null, 2)
  );
  console.log(
    "Device Name:",
    policyDetailsInfo?.DeviceName || policyDetailsInfo?.DeviceModel
  );
  console.log(
    "Device Type:",
    policyDetailsInfo?.DeviceType || policyDetailsInfo?.Category
  );
  console.log(
    "IMEI/Serial:",
    policyDetailsInfo?.IMEI || policyDetailsInfo?.SerialNumber
  );
  console.log(
    "Purchase Date:",
    policyDetailsInfo?.PurchaseDate || policyDetailsInfo?.DevicePurchaseDate
  );
  console.log(
    "Coverage Amount:",
    policyDetailsInfo?.CoverageAmount || policyDetailsInfo?.SumInsured
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("=== END DEBUG ===");

  // Helper function to format dates as DD/MM/YYYY
  const formatDateAsNumbers = (dateStr) => {
    if (!dateStr) return "N/A";

    try {
      let date;

      // Handle different date formats
      if (typeof dateStr === "string" && dateStr.includes("/")) {
        // Already in DD/MM/YYYY format
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${
            parts[2]
          }`;
        }
      }

      // Check if it's a unix timestamp (number or string number)
      const timestamp =
        typeof dateStr === "string" ? parseInt(dateStr) : dateStr;
      if (!isNaN(timestamp) && timestamp > 1000000000) {
        // Unix timestamp in seconds, convert to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        // Try parsing as regular date
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) {
        return dateStr; // Return original if can't parse
      }

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr || "N/A";
    }
  };

  // Helper function to format coverage amount
  const formatCoverageAmount = (amount) => {
    if (!amount) return translate("N/A", "غير متوفر");

    if (typeof amount === "number") {
      return `BHD ${amount.toFixed(3)}`;
    }

    return amount;
  };

  // Create cyber security policy card info
  const policyCardInfo = {
    ModelYear: "",
    Make: "Cyber Security Policy",
    Model:
      policyDetailsInfo?.DeviceName ||
      policyDetailsInfo?.DeviceModel ||
      "Device",
    CPR: "",
    PolicyNumber: policyDetailsInfo?.PolicyNumber || "--",
    RegistrationNumber: "", // Not applicable for cyber security
    SumInsured:
      policyDetailsInfo?.SumInsured || policyDetailsInfo?.CoverageAmount || 0.0,
    PlanName: policyDetailsInfo?.PlanName || "--",
    ProductCode: "0214", // Cyber Security product code
    ProductName: "Cyber Security",
    StartDate: formatDateAsNumbers(
      policyDetailsInfo?.FromDateStr || policyDetailsInfo?.StartDate
    ),
    EndDate: formatDateAsNumbers(
      policyDetailsInfo?.ToDateStr || policyDetailsInfo?.EndDate
    ),
    FromDateStr: policyDetailsInfo?.FromDateStr?.split(" ")[0] || "--",
    ToDateStr: policyDetailsInfo?.ToDateStr?.split(" ")[0] || "--",
    Branch: getClientSource(),
    MainChannel: "Direct Bahrain",
    Renewed: policyDetailsInfo?.IsRenewed,
    PolicyStatus: "Active",
    IsRenewable: false,
  };

  const continueClickHandler = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={true}
      show_close_btn={true}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHandler}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHandler}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg pb-20 max767:p-5 max767:mt-0 max767:pt-0 max767:pb-[50px]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your cyber security policy details before proceeding with cancellation.",
                "يرجى مراجعة تفاصيل وثيقة الأمان السيبراني الخاصة بك قبل المتابعة مع الإلغاء."
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول"
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <PolicyCard
                className="mb-4"
                policy_name="Cyber Security Insurance"
                policy_label="Cyber Security Policy"
                policy_number={policyDetailsInfo?.PolicyNumber || "N/A"}
                from_date={policyCardInfo.FromDateStr}
                to_date={policyCardInfo.ToDateStr}
                policy_bg_image={policyCardInfo.policy_bg_image}
                expire_days={policyCardInfo.expire_days}
                is_text_dark={policyCardInfo.is_text_dark}
                mapping_key={policyCardInfo.id}
                data={policyCardInfo}
                disableCardClick
              />

              {/* Cyber Security specific table structure */}
              <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px] max767:border-0">
                <tbody>
                  {/* 1. Device Name */}
                  <tr className="block md:table-row md:border-b max767:border-b-[#DDE2E4] max767:border-b max767:border-solid">
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:pl-0">
                      {translate("Device Name", "اسم الجهاز")}
                    </td>
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:text-[#48535B] max767:font-medium max767:pl-0">
                      {policyDetailsInfo?.DeviceName ||
                        policyDetailsInfo?.DeviceModel ||
                        translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 2. Device Type */}
                  <tr className="block md:table-row md:border-b max767:border-b-[#DDE2E4] max767:border-b max767:border-solid">
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:pl-0">
                      {translate("Device Type", "نوع الجهاز")}
                    </td>
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:text-[#48535B] max767:font-medium max767:pl-0">
                      {policyDetailsInfo?.DeviceType ||
                        policyDetailsInfo?.Category ||
                        translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 3. IMEI/Serial Number */}
                  <tr className="block md:table-row md:border-b max767:border-b-[#DDE2E4] max767:border-b max767:border-solid">
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:pl-0">
                      {translate("IMEI/Serial Number", "رقم IMEI/المسلسل")}
                    </td>
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:text-[#48535B] max767:font-medium max767:pl-0">
                      {policyDetailsInfo?.IMEI ||
                        policyDetailsInfo?.SerialNumber ||
                        translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 4. Purchase Date */}
                  <tr className="block md:table-row md:border-b max767:border-b-[#DDE2E4] max767:border-b max767:border-solid">
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:pl-0">
                      {translate("Purchase Date", "تاريخ الشراء")}
                    </td>
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:text-[#48535B] max767:font-medium max767:pl-0">
                      {formatDateAsNumbers(
                        policyDetailsInfo?.PurchaseDate ||
                          policyDetailsInfo?.DevicePurchaseDate
                      )}
                    </td>
                  </tr>

                  {/* 5. Coverage Amount */}
                  <tr className="block md:table-row md:border-b max767:border-b-[#DDE2E4] max767:border-b max767:border-solid">
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:pl-0">
                      {translate("Coverage Amount", "مبلغ التغطية")}
                    </td>
                    <td className="py-2 px-4 md:border-b md:border-r text-[#5B6871] text-sm font-medium leading-6 block md:table-cell max767:text-[#48535B] max767:font-medium max767:pl-0">
                      {formatCoverageAmount(
                        policyDetailsInfo?.CoverageAmount ||
                          policyDetailsInfo?.SumInsured
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default CyberSecurityCancellationReviewPage;
